"use client"

import { useState, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { dateFormat_YY_MM_DY_timeStamp } from "../../../shared/utils/utils"
import type { ILeadershipRating } from "../../../../shared/models/three-sixty-feedback-models/LeadershipRating"
import type { IValueRating } from "../../../../shared/models/three-sixty-feedback-models/ValueRating"
import { RESUBMISSION_EMAIL } from "../../../../shared/functions/mailMessages"
import GenerateRatingsTablePDF from "./GenerateRatingsTablePDF"
import GeneratePrintablePage from "./GeneratePrintablePage"
import {
  Flag,
  RotateCcw,
  Download,
  Printer,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Crown,
  Heart,
  Briefcase,
  X,
  CheckCircle,
} from "lucide-react"

interface ILeadershipRatings {
  overallRating: number
  id: string
  rateeId: string
  dimension: string
  isflagged: boolean
  department: string
  description: string
  criteria: {
    [raterId: string]: {
      additionalComment?: string
      stopDoing: string
      startDoing: string
      continueDoing: string
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number
        }
      }
      leadershipScore?: number
    }
  }
  date: number
  raterId?: string
  templateId?: string
  heading?: string
  reasonForRequest?: string
  dueDate?: string
  values?: {}
  archived?: false
}

interface IProp {
  data: (ILeadershipRating | ILeadershipRatings)[]
  description: string
}

const AdminFlagRatingsTable = observer(({ data, description }: IProp) => {
  const { api, store, ui } = useAppContext()
  const me = store.auth.meJson?.uid
  const user = store.user.selected
  const templates = store.templates.all.map((template) => template.asJson)

  const [openReRatingDialog, setOpenReRatingDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState<ILeadershipRatings | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const fetchData = async () => {
    await api.user.getAll()
    await api.templates.getAll()
    await api.ratingAssignments.getAll()
  }

  useEffect(() => {
    fetchData()
  }, [api.user, api.templates])

  const getTemplateTitle = (templateId: string): string => {
    const foundTemplate = templates.find((template) => template.id === templateId)
    return foundTemplate ? foundTemplate.title : templateId
  }

  const getDimensionIcon = (dimension: string) => {
    if (dimension === "Values") return <Heart className="h-4 w-4 text-blue-600" />
    if (dimension === "Leadership") return <Crown className="h-4 w-4 text-purple-600" />
    return <Briefcase className="h-4 w-4 text-green-600" />
  }

  // Filter and search data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.dimension.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTemplateTitle(item.dimension).toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.values(item.criteria).some(
          (criteria) =>
            criteria.startDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.stopDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.continueDoing?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            criteria.additionalComment?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      return matchesSearch
    })
  }, [data, searchTerm])

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

  const handleRequestReRating = async () => {
    if (!selectedRow) return

    if (selectedRow.dimension === "Values") {
      if (selectedRow.id) {
        const selectedRating = store.valueRating.getItemById(selectedRow.id)?.asJson
        if (selectedRating) {
          const currentValueRating: IValueRating = {
            ...selectedRating,
            adminApproval: "Approved for Re-rating",
            resubmission: "To be rated",
          }
          await api.valueRating.update(currentValueRating, selectedRating.rateeId, description)
          const { MY_SUBJECT, MY_BODY } =
            user && me ? RESUBMISSION_EMAIL(user.email, me) : { MY_SUBJECT: "", MY_BODY: "" }
          try {
            await api.mail.sendMail([selectedRating.rateeId], MY_SUBJECT, MY_BODY, "")
            ui.snackbar.load({
              id: Date.now(),
              type: "success",
              message: "Email sent.",
              timeoutInMs: 10000,
            })
          } catch (error) {
            console.log("Error sending email:", error)
            ui.snackbar.load({
              id: Date.now(),
              type: "warning",
              message: "Email not sent.",
              timeoutInMs: 10000,
            })
          }
        }
      }
    } else if (selectedRow.dimension === "Leadership") {
      if (selectedRow.id) {
        const selectedRating = store.leadershipRating.getItemById(selectedRow.id)?.asJson
        if (selectedRating) {
          const currentValueRating: ILeadershipRating = {
            ...selectedRating,
            adminApproval: "Approved for Re-rating",
            resubmission: "To be rated",
          }
          await api.leadershipRating.update(currentValueRating, selectedRating.rateeId, description)
          const { MY_SUBJECT, MY_BODY } =
            user && me ? RESUBMISSION_EMAIL(user.email, me) : { MY_SUBJECT: "", MY_BODY: "" }
          try {
            await api.mail.sendMail([selectedRating.rateeId], MY_SUBJECT, MY_BODY, "")
            ui.snackbar.load({
              id: Date.now(),
              type: "success",
              message: "Email sent.",
              timeoutInMs: 10000,
            })
          } catch (error) {
            console.log("Error sending email:", error)
            ui.snackbar.load({
              id: Date.now(),
              type: "warning",
              message: "Email not sent.",
              timeoutInMs: 10000,
            })
          }
        }
      }
    } else {
      console.log("Cannot request re-rating for this dimension:", selectedRow.dimension)
    }
    setOpenReRatingDialog(false)
  }

  const handleReRatingClick = (row: ILeadershipRatings) => {
    setSelectedRow(row)
    setOpenReRatingDialog(true)
  }

  const createPDF = GenerateRatingsTablePDF(data)
  const createPrintablePage = GeneratePrintablePage(data)

  const handleExportPDF = () => {
    createPDF()
  }

  const handlePrint = () => {
    createPrintablePage()
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Flag className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Admin Flagged Ratings Overview</h3>
            <p className="text-slate-600">Manage flagged ratings and resubmissions</p>
          </div>
        </div>
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search ratings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          {/* Export buttons */}
          <button
            onClick={handlePrint}
            className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="Print"
          >
            <Printer className="h-5 w-5" />
          </button>
          <button
            onClick={handleExportPDF}
            className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            title="Export PDF"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-slate-600 px-6">
        <span>
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length}{" "}
          ratings
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value))
            setCurrentPage(1)
          }}
          className="px-2 py-1 border border-slate-300 rounded text-sm"
        >
          <option value={5}>5 per page</option>
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Dimension
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Start Doing
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Stop Doing
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Continue Doing
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Comments
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {paginatedData.map((row, index) => {
              const criteria =
                row.criteria && Object.keys(row.criteria).length > 0 ? row.criteria[Object.keys(row.criteria)[0]] : null
              return (
                <tr
                  key={row.id}
                  className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-slate-25"}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-900">{dateFormat_YY_MM_DY_timeStamp(row.date)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getDimensionIcon(row.dimension)}
                      <span className="text-sm font-medium text-slate-900">
                        {row.dimension === "Values" || row.dimension === "Leadership"
                          ? row.dimension
                          : getTemplateTitle(row.dimension)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.startDoing || ""}>
                      {criteria?.startDoing || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.stopDoing || ""}>
                      {criteria?.stopDoing || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.continueDoing || ""}>
                      {criteria?.continueDoing || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-900 max-w-xs truncate" title={criteria?.additionalComment || ""}>
                      {criteria?.additionalComment || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          row.isflagged ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        <Flag className="h-3 w-3" />
                        {row.isflagged ? "Flagged" : "Normal"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleReRatingClick(row)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      title="Request Re-rating"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Empty state */}
        {paginatedData.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No ratings found</h3>
            <p className="text-slate-600">Try adjusting your search criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      currentPage === pageNum
                        ? "bg-red-600 text-white"
                        : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Re-rating Confirmation Dialog */}
      {openReRatingDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Request Re-rating</h3>
              <button
                onClick={() => setOpenReRatingDialog(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-6">Are you sure you want to send this rating for resubmission?</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setOpenReRatingDialog(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestReRating}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

export default AdminFlagRatingsTable
